<template>
  <div class="enterprise-detail">
    <div class="header">
      <el-button icon="el-icon-arrow-left" @click="goBack">返回</el-button>
      <h2>企业详情</h2>
      <div class="actions">
        <el-button type="primary" icon="el-icon-edit" @click="handleEdit">编辑企业</el-button>
        <el-button type="success" icon="el-icon-document" @click="showChangeHistory">变更历史</el-button>
      </div>
    </div>

    <el-card v-loading="loading">
      <div class="enterprise-info" v-if="enterprise">
        <div class="basic-info">
          <div class="logo-section">
            <img v-if="enterprise.logo" :src="$http.getFullImageUrl(enterprise.logo)" class="enterprise-logo">
            <div v-else class="no-logo">暂无Logo</div>
          </div>
          <div class="info-section">
            <h3>{{ enterprise.name }}</h3>
            <div class="info-grid">
              <div class="info-item">
                <label>信用评级：</label>
                <span>{{ enterprise.creditRating || '-' }}</span>
              </div>
              <div class="info-item">
                <label>统一信用代码：</label>
                <span>{{ enterprise.creditCode || '-' }}</span>
              </div>
              <div class="info-item">
                <label>法定代表人：</label>
                <span>{{ enterprise.legalRepresentative || '-' }}</span>
              </div>
              <div class="info-item">
                <label>联系电话：</label>
                <span>{{ enterprise.phone || '-' }}</span>
              </div>
              <div class="info-item">
                <label>注册资本：</label>
                <span>{{ enterprise.registeredCapital ? enterprise.registeredCapital + '万元' : '-' }}</span>
              </div>
              <div class="info-item">
                <label>注册日期：</label>
                <span>{{ enterprise.registrationDate || '-' }}</span>
              </div>
              <div class="info-item">
                <label>企业状态：</label>
                <el-tag :type="enterprise.status === 'ACTIVE' ? 'success' : 'info'">
                  {{ enterprise.status === 'ACTIVE' ? '正常' : '停用' }}
                </el-tag>
              </div>
              <div class="info-item">
                <label>创建时间：</label>
                <span>{{ formatDateTime(enterprise.createdAt) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-sections">
          <el-row :gutter="20" style="margin-bottom: 20px;">
             <el-col :span="12">
               <el-card header="资质认证">
                  <el-row :gutter="10">
                    <el-col :span="8">
                      <div class="cert-item">
                        <div class="cert-header">
                          <span class="cert-label">营业执照</span>
                          <el-link v-if="enterprise.licenseUrl" type="primary" icon="el-icon-download" :underline="false" @click="downloadFile(enterprise.licenseUrl, '营业执照')">下载</el-link>
                        </div>
                        <el-image 
                          style="width: 100%; height: 100px"
                          :src="$http.getFullImageUrl(enterprise.licenseUrl)" 
                          :preview-src-list="[$http.getFullImageUrl(enterprise.licenseUrl)]"
                          fit="cover">
                          <div slot="error" class="image-slot">
                            <i class="el-icon-picture-outline"></i>
                            <span>文件丢失</span>
                          </div>
                        </el-image>
                      </div>
                    </el-col>
                    <el-col :span="8">
                      <div class="cert-item">
                        <div class="cert-header">
                          <span class="cert-label">身份证(正)</span>
                          <el-link v-if="enterprise.idCardFrontUrl" type="primary" icon="el-icon-download" :underline="false" @click="downloadFile(enterprise.idCardFrontUrl, '身份证正面')">下载</el-link>
                        </div>
                        <el-image 
                          style="width: 100%; height: 100px"
                          :src="$http.getFullImageUrl(enterprise.idCardFrontUrl)" 
                          :preview-src-list="[$http.getFullImageUrl(enterprise.idCardFrontUrl)]"
                          fit="cover">
                          <div slot="error" class="image-slot">
                            <i class="el-icon-picture-outline"></i>
                            <span>文件丢失</span>
                          </div>
                        </el-image>
                      </div>
                    </el-col>
                    <el-col :span="8">
                      <div class="cert-item">
                        <div class="cert-header">
                          <span class="cert-label">身份证(反)</span>
                          <el-link v-if="enterprise.idCardBackUrl" type="primary" icon="el-icon-download" :underline="false" @click="downloadFile(enterprise.idCardBackUrl, '身份证反面')">下载</el-link>
                        </div>
                        <el-image 
                          style="width: 100%; height: 100px"
                          :src="$http.getFullImageUrl(enterprise.idCardBackUrl)" 
                          :preview-src-list="[$http.getFullImageUrl(enterprise.idCardBackUrl)]"
                          fit="cover">
                          <div slot="error" class="image-slot">
                            <i class="el-icon-picture-outline"></i>
                            <span>文件丢失</span>
                          </div>
                        </el-image>
                      </div>
                    </el-col>
                  </el-row>
               </el-card>
             </el-col>
             <el-col :span="12">
               <el-card header="法人信息">
                 <p><strong>法人姓名：</strong>{{ enterprise.legalRepresentative || '-' }}</p>
                 <p><strong>身份证号：</strong>{{ enterprise.legalPersonIdCard || '-' }}</p>
               </el-card>
             </el-col>
          </el-row>
        
          <el-row :gutter="20">
            <el-col :span="12">
              <el-card header="业务范围">
                <p>{{ enterprise.businessScope || '暂无信息' }}</p>
              </el-card>
            </el-col>
            <el-col :span="12">
              <el-card header="提供服务">
                <p>{{ enterprise.services || '暂无信息' }}</p>
              </el-card>
            </el-col>
          </el-row>
        </div>

        <!-- 交易记录 -->
        <h3 style="margin-top: 30px; font-size: 20px; border-bottom: 1px solid #ebeef5; padding-bottom: 10px; margin-bottom: 20px;">交易记录</h3>
        <el-table :data="transactions" v-loading="transactionsLoading" border style="width: 100%" show-summary :summary-method="getSummaries">
          <el-table-column prop="id" label="订单ID" width="80"></el-table-column>
          <el-table-column prop="createdAt" label="成交时间" width="160">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="serviceName" label="服务名称" min-width="150"></el-table-column>
          <el-table-column prop="customerName" label="下单人" width="120"></el-table-column>
          <el-table-column prop="amount" label="交易金额" width="120" align="right">
            <template slot-scope="scope">
              <span :class="{ 'negative-amount': scope.row.amount < 0 }">
                ¥{{ formatAmount(scope.row.amount) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'PAID' ? 'success' : 'info'">
                {{ scope.row.status === 'PAID' ? '已支付' : scope.row.status }}
              </el-tag>
            </template>
          </el-table-column>

        </el-table>

        <!-- 交易记录分页 -->
        <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="pagination.page"
          :page-sizes="[10, 20, 50]" :page-size="pagination.size" layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total" style="margin-top: 15px; text-align: right"></el-pagination>

      </div>
    </el-card>

    <!-- 变更历史对话框 -->
    <el-dialog
      title="企业变更历史"
      :visible.sync="historyDialogVisible"
      width="80%"
      top="5vh"
    >
      <el-table
        :data="changeHistory"
        v-loading="historyLoading"
        border
        style="width: 100%"
      >
        <el-table-column prop="fieldName" label="变更字段" width="120">
          <template slot-scope="scope">
            {{ getFieldLabel(scope.row.fieldName) }}
          </template>
        </el-table-column>
        <el-table-column prop="oldValue" label="变更前" min-width="150">
          <template slot-scope="scope">
            <span class="old-value">{{ scope.row.oldValue || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="newValue" label="变更后" min-width="150">
          <template slot-scope="scope">
            <span class="new-value">{{ scope.row.newValue || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="changeType" label="变更类型" width="100">
          <template slot-scope="scope">
            {{ getChangeTypeLabel(scope.row.changeType) }}
          </template>
        </el-table-column>
        <el-table-column prop="operatorName" label="操作人" width="100"></el-table-column>
        <el-table-column prop="changeDate" label="变更时间" width="160">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.changeDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="remarks" label="备注" min-width="120"></el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'EnterpriseDetail',
  data() {
    return {
      enterprise: null,
      statistics: {},
      changeHistory: [],
      transactions: [],
      loading: false,
      transactionsLoading: false,
      historyLoading: false,
      historyDialogVisible: false,
      pagination: {
        page: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    enterpriseId() {
      return this.$route.params.id
    }
  },
  mounted() {
    this.loadEnterpriseDetail()
    this.loadTransactions()
  },
  methods: {
    async loadEnterpriseDetail() {
      try {
        this.loading = true
        const response = await this.$http.get(`/enterprise-management/enterprises/${this.enterpriseId}`)
        if (response.success) {
          this.enterprise = response.data
        } else {
          this.$message.error('加载企业详情失败: ' + response.message)
        }
      } catch (error) {
        console.error('加载企业详情失败:', error)
        this.$message.error('加载企业详情失败')
      } finally {
        this.loading = false
      }
    },

    async loadTransactions() {
      try {
        this.transactionsLoading = true
        const params = { enterpriseId: this.enterpriseId, status: 'PAID' }
        const response = await this.$http.get('/service-orders', { params })
        if (response.success) {
          const allData = response.data || []
          this.pagination.total = response.total || allData.length || 0
          if (response.total && response.total > allData.length) {
            this.transactions = allData
          } else {
            const start = (this.pagination.page - 1) * this.pagination.size
            const end = this.pagination.page * this.pagination.size
            this.transactions = allData.slice(start, end)
          }
        }
      } catch (error) {
        console.error('加载交易记录失败:', error)
      } finally {
        this.transactionsLoading = false
      }
    },
    
    handleSizeChange(size) {
      this.pagination.size = size
      this.loadTransactions()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadTransactions()
    },

    formatAmount(amount) {
      if (amount == null) return '0.00'
      return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        if (column.property === 'amount') {
          const values = data.map(item => Number(item[column.property]))
          if (!values.every(value => isNaN(value))) {
            sums[index] = '¥' + this.formatAmount(values.reduce((prev, curr) => {
              const value = Number(curr)
              if (!isNaN(value)) {
                return prev + curr
              } else {
                return prev
              }
            }, 0))
          } else {
            sums[index] = '¥0.00'
          }
        } else {
          sums[index] = ''
        }
      })
      return sums
    },

    async showChangeHistory() {
      try {
        this.historyLoading = true
        this.historyDialogVisible = true
        const response = await this.$http.get(`/enterprise-management/enterprises/${this.enterpriseId}/change-history`)
        if (response.success) {
          this.changeHistory = response.data || []
        } else {
          this.$message.error('加载变更历史失败: ' + response.message)
        }
      } catch (error) {
        console.error('加载变更历史失败:', error)
        this.$message.error('加载变更历史失败')
      } finally {
        this.historyLoading = false
      }
    },

    handleEdit() {
      this.$router.push(`/admin/enterprise-management/edit/${this.enterpriseId}`)
    },

    goBack() {
      this.$router.go(-1)
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    },
    
    getFieldLabel(fieldName) {
      const fieldMap = {
        'name': '企业名称',
        'companyName': '企业名称',
        'creditCode': '统一信用代码',
        'creditRating': '信用评级',
        'legalRepresentative': '法人代表',
        'legalPerson': '法人代表',
        'legalPersonIdCard': '法人身份证',
        'phone': '联系电话',
        'registeredCapital': '注册资本',
        'registrationDate': '注册日期',
        'businessScope': '经营范围',
        'services': '提供服务',
        'logo': '企业Logo',
        'licenseUrl': '营业执照',
        'idCardFrontUrl': '身份证正面',
        'idCardBackUrl': '身份证反面',
        'status': '状态',
        'address': '地址'
      }
      return fieldMap[fieldName] || fieldName
    },

    getChangeTypeLabel(changeType) {
      const typeMap = {
        'CREATE': '创建',
        'UPDATE': '更新',
        'DELETE': '删除',
        'AUDIT': '审核',
        'STATUS_CHANGE': '状态变更'
      }
      return typeMap[changeType] || changeType
    },

    downloadFile(url, type) {
      if (!url) return
      const filename = `${this.enterprise.name}_${type}.${url.split('.').pop()}`
      this.$http.downloadImage(url, filename)
    }
  }
}
</script>

<style scoped lang="scss">
.negative-amount {
  color: #f56c6c;
}

.enterprise-detail {
  .header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    gap: 15px;
    
    h2 {
      margin: 0;
      flex: 1;
      font-size: 24px;
      color: #333;
    }
    
    .actions {
      display: flex;
      gap: 10px;
    }
  }

  .enterprise-info {
    .basic-info {
      display: flex;
      margin-bottom: 30px;
      
      .logo-section {
        margin-right: 30px;
        
        .enterprise-logo {
          width: 200px;
          height: 120px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #ddd;
        }
        
        .no-logo {
          width: 200px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          border: 1px dashed #ddd;
          border-radius: 8px;
          color: #999;
        }
      }
      
      .info-section {
        flex: 1;
        
        h3 {
          margin: 0 0 20px 0;
          font-size: 28px;
          color: #333;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          
          .info-item {
            display: flex;
            
            label {
              font-weight: bold;
              color: #666;
              min-width: 100px;
            }
            
            span {
              color: #333;
            }
          }
        }
      }
    }
    
    .detail-sections {
      margin-bottom: 30px;
      
      .el-card {
        ::v-deep .el-card__header {
          background: #f8f9fa;
          font-weight: bold;
        }
        
        p {
          margin: 0;
          line-height: 1.6;
          color: #666;
        }
      }
    }
    
    .statistics {
      .stat-item {
        text-align: center;
        
        .stat-number {
          font-size: 32px;
          font-weight: bold;
          color: #409EFF;
          margin-bottom: 8px;
        }
        
        .stat-label {
          color: #666;
          font-size: 14px;
        }
      }
    }
  }
}

.old-value {
  color: #f56c6c;
  text-decoration: line-through;
}

.new-value {
  color: #67c23a;
  font-weight: bold;
}

@media screen and (max-width: 768px) {
  .enterprise-detail {
    .header {
      flex-direction: column;
      align-items: flex-start;
      
      .actions {
        width: 100%;
        justify-content: space-between;
      }
    }
    
    .enterprise-info {
      .basic-info {
        flex-direction: column;
        
        .logo-section {
          margin-right: 0;
          margin-bottom: 20px;
          text-align: center;
        }
        
        .info-section {
          .info-grid {
            grid-template-columns: 1fr;
          }
        }
      }
    }
  }
}

.cert-item {
  .cert-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    
    .cert-label {
      font-size: 13px;
      color: #666;
    }
    
    .el-link {
      font-size: 12px;
    }
  }
  
  .image-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 12px;
    
    i {
      font-size: 20px;
      margin-bottom: 5px;
    }
  }
}
</style>
